import { Fragment, type ReactNode } from 'react';

/**
 * Zahl-+-Einheit-Typografie, zentral. Eine Zahl und ihre nachfolgende Einheit
 * dürfen NIE durch einen Zeilenumbruch getrennt werden (sonst rutscht z.B. das
 * „€", „%" oder „m²" allein in die nächste Zeile). Lösung: geschütztes
 * Leerzeichen zwischen Zahl-Run und Einheit PLUS die zentrale CSS-Klasse
 * `.nowrap` (`white-space: nowrap`, in locari.css).
 *
 * Geschützt wird eine kuratierte Einheiten-Liste (Symbole + Einheiten-Wörter
 * inkl. Flexion), NICHT beliebige Substantive – „5 Wohnungen" / „251 Anfragen"
 * dürfen weiterhin normal umbrechen.
 *
 * Zwei Wege:
 *   <Eur>{betrag}</Eur>      → explizit im JSX (Geldbeträge in neuer Copy)
 *   <UnitText>{string}</…>   → schützt Zahl+Einheit in bestehendem Fließtext
 *                              (FAQ-Antworten, Pricing-Bodies) automatisch.
 */

const NBSP = ' ';

/** Betrag + geschütztes Leerzeichen + €, umbruchsicher (`.nowrap`). */
export function Eur({ children }: { children: ReactNode }) {
  return (
    <span className="nowrap">
      {children}
      {NBSP}€
    </span>
  );
}

/* FINALE Regex (Senior-Review V2, verbatim). Erfasst „Zahl + Einheit":
   - Zahl-Run inkl. Tausender-Trennern (39 · 6,3 · 1.000 · 1 000 FR) und
     optionaler Magnitude („20 Mio").
   - Einheit als Symbol (€ % m² km) ODER Einheiten-Wort (lang→kurz, +Flexion
     en|n|e|s), abgeschlossen durch eine Buchstaben-Wortgrenze (kein „Tag" in
     „Tagung", kein „h" in „Häuser").
   `\p{L}` benötigt das `u`-Flag. */
const SEP = '[ \\u00a0\\u202f]';
const RUN = `\\d[\\d.,]*(?:${SEP}\\d{3})*`; // 39 · 6,3 · 1.000 · 1 000
const MAG = `(?:${SEP}(?:Mio|Mrd|Tsd))?`; // „20 Mio" als Teil des Runs
const SYM = `€|%|m²|km`; // Symbol-Einheiten (keine Wortgrenze nötig)
const WORD = `Minuten|Min|Stunden|Stunde|Std|Tage|Tag|Monate|Monat|Jahre|Jahr|qm|h|Mio|Mrd|Tsd`;
const UNIT_RE = new RegExp(
  `(${RUN}${MAG})${SEP}(?:(${SYM})|((?:${WORD})(?:en|n|e|s)?)(?![\\p{L}]))`,
  'gu',
);

/** Wandelt einen String in ReactNode um, in dem jeder „Zahl Einheit"-Token in
 *  ein umbruchsicheres `.nowrap`-Span mit geschütztem Leerzeichen gehüllt ist. */
export function formatUnitText(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  UNIT_RE.lastIndex = 0;
  while ((match = UNIT_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const unit = match[2] ?? match[3];
    parts.push(
      <span className="nowrap" key={match.index}>
        {match[1]}
        {NBSP}
        {unit}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  if (parts.length === 0) return text;

  return parts.map((part, i) => <Fragment key={i}>{part}</Fragment>);
}

/** Komponenten-Variante von {@link formatUnitText} für `string`-Children. */
export function UnitText({ children }: { children: string }) {
  return <>{formatUnitText(children)}</>;
}

/**
 * Schützt einen `ReactNode`-Prop am Render-Punkt: ist der Wert ein String, wird
 * jeder „Zahl Einheit"-Token umbruchsicher gewrappt; JSX (das eine Einheit
 * bereits via `<Eur>`/`<span class="nowrap">` geschützt haben sollte) bleibt
 * unangetastet durchgereicht.
 */
export function protectUnitsNode(node: ReactNode): ReactNode {
  return typeof node === 'string' ? formatUnitText(node) : node;
}
